import { DocumentNode, gql } from '@apollo/client';
import { HStack, Image, Text, VStack } from '@chakra-ui/react';

import { IRequestDemoView } from '@/types/analytics';
import { ImageFallback } from '@/imports/ui/chakra/components/ImageFallback';
import { RequestDemoButton } from '@/imports/ui/components/RequestDemoButton';

import { SimpleEventMobileSponsors_EventFragment } from './graphql-types/SimpleEventMobileSponsors_EventFragment';

export const SimpleEventMobileSponsors: React.FC<{
  eventData?: SimpleEventMobileSponsors_EventFragment;
  origin: {
    conferenceBoothId?: string;
    sponsoredEventId?: string;
    sponsoredConferenceSessionId?: string;
    hostedEventId?: string;
    sponsorPage?: boolean;
  };
  analytics: {
    view: IRequestDemoView;
    tier: string;
  };
}> & { eventFragment: DocumentNode } = ({ eventData, origin, analytics }) => {
  if (eventData?.__typename !== 'SimpleEventV2') {
    return null;
  }

  const keySponsor = eventData.simpleEventSponsors?.keySponsor;
  const additionalSponsors =
    eventData.simpleEventSponsors?.additionalSponsors || [];

  const keySponsorImageProxy =
    keySponsor?.sponsorship?.ad?.adImage?.mobileImageProxy ||
    keySponsor?.profilePicture?.mobileImageProxy;

  const organizationId = keySponsor?._id;

  const registerInterestTitle =
    keySponsor?.sponsorship?.ad?.registerInterestTitle;

  return (
    <VStack spacing="2">
      {keySponsor && (
        <VStack
          w="full"
          backgroundColor="white"
          p="4"
          boxShadow="md"
          spacing="4"
        >
          {keySponsorImageProxy ? (
            <Image
              src={keySponsorImageProxy}
              w="100px"
              borderRadius="lg"
              alt="key sponsor"
            />
          ) : (
            <ImageFallback borderRadius="lg" />
          )}
          <Text fontWeight="semibold" fontSize="xl">
            {keySponsor?.displayName}
          </Text>
          <Text>{keySponsor?.sponsorship?.ad?.body}</Text>
          <RequestDemoButton
            options={{
              organizationId: organizationId || '',
              origin: origin,
              registerInterestTitle: registerInterestTitle || 'Request Demo',
              analytics: analytics,
            }}
            w="full"
          />
        </VStack>
      )}

      {additionalSponsors.length > 0 && (
        <VStack
          p="4"
          backgroundColor="white"
          shadow={1}
          w="100%"
          boxShadow="md"
          alignItems="flex-start"
          spacing="2"
        >
          <Text fontWeight="semibold">Additional Sponsors</Text>
          {additionalSponsors.map((sponsor) => {
            const additionalSponsorImageProxy =
              sponsor.sponsorship?.ad?.adImage?.mobileImageProxy ||
              sponsor.profilePicture?.mobileImageProxy;
            return (
              <HStack key={sponsor._id}>
                {additionalSponsorImageProxy ? (
                  <Image
                    src={additionalSponsorImageProxy}
                    borderRadius="lg"
                    boxSize="16"
                    alt="additional sponsor"
                  />
                ) : (
                  <ImageFallback boxSize="16" borderRadius="lg" />
                )}
                <Text noOfLines={2}>{sponsor.displayName}</Text>
              </HStack>
            );
          })}
        </VStack>
      )}
    </VStack>
  );
};

SimpleEventMobileSponsors.eventFragment = gql`
  fragment SimpleEventMobileSponsors_EventFragment on EventV2 {
    _id
    ... on SimpleEventV2 {
      _id
      simpleEventSponsors {
        keySponsor {
          ... on Organization {
            _id
            sponsorship {
              currentTier {
                analyticsTierType
              }
              requestDemoTitle
              ad {
                _id
                registerInterestTitle
                body
                adImage {
                  mobileImageProxy: imageProxy(
                    input: { h: 144, w: 146, fit: "crop" }
                  )
                }
              }
            }
            displayName
            aboutUs
            profilePicture {
              mobileImageProxy: imageProxy(
                input: { h: 144, w: 146, fit: "crop" }
              )
            }
          }
        }
        additionalSponsors {
          ... on Organization {
            _id
            sponsorship {
              currentTier {
                analyticsTierType
              }
              ad {
                _id
                adImage {
                  mobileImageProxy: imageProxy(
                    input: { h: 60, w: 60, fit: "crop" }
                  )
                }
              }
            }
            displayName
            profilePicture {
              mobileImageProxy: imageProxy(input: { h: 60, w: 60, fit: "crop" })
            }
          }
        }
      }
    }
  }
`;
